import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  ExternalLink,
  FileText,
  Link2,
  ShoppingCart,
  Swords,
  X,
} from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { EmptyState } from "@/components/intelligence/EmptyState";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ordenesCompraService } from "@/services/ordenes-compra.service";
import { competitorsService } from "@/services/competitors.service";
import { formatCLP } from "@/lib/mock-data";
import { mercadoPublicoUrl } from "@/lib/licitaciones";
import { levelLabel } from "@/lib/scoring/scoring";
import type { OrdenCompraDetalle } from "@/types";

export const Route = createFileRoute("/ordenes-compra/$codigo")({
  head: () => ({
    meta: [
      { title: "Detalle de orden de compra — LicitaPro" },
      {
        name: "description",
        content: "Ítems, montos, proveedor y licitación de origen de la orden de compra.",
      },
    ],
  }),
  component: OrdenCompraDetallePage,
});

function fmtFecha(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-CL", { timeZone: "America/Santiago" });
}

/** "enviada_proveedor" → "Enviada proveedor". */
function legibleEstado(estado: string | null): string {
  if (!estado) return "—";
  const t = estado.replace(/_/g, " ").trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Sigla del tipo de OC de Mercado Público → etiqueta. */
const TIPOS_OC: Record<string, string> = {
  SE: "Sin emisión automática",
  CM: "Convenio Marco",
  AG: "Compra Ágil",
  LP: "Licitación Pública",
  LE: "Licitación Pública (LE)",
  LQ: "Licitación Pública (LQ)",
  LR: "Licitación Pública (LR)",
  L1: "Licitación Pública (L1)",
  TR: "Trato Directo",
  CO: "Contrato",
  B2: "Licitación Privada",
  RC: "Compra por Reposición",
};

function legibleTipo(tipo: string | null): string {
  if (!tipo) return "—";
  const sigla = tipo.trim().toUpperCase();
  return TIPOS_OC[sigla] ? `${TIPOS_OC[sigla]} (${sigla})` : sigla;
}


/** Monto nulo = MP aún no lo entregó en nuestra copia; no es "sin monto". */
function fmtMonto(valor: number | null): string {
  return valor != null ? formatCLP(valor) : "Pendiente de sincronizar";
}

function Dato({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

/** Los 3 montos de la orden pesan distinto: el Total se resalta, no es "un dato más". */
function KpiTile({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div
      className={
        accent
          ? "rounded-lg border border-primary/30 bg-primary/5 p-3"
          : "rounded-lg border border-border bg-muted/30 p-3"
      }
    >
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div
        className={
          accent
            ? "text-lg font-semibold text-primary tabular-nums"
            : "text-sm font-medium tabular-nums"
        }
      >
        {value}
      </div>
    </div>
  );
}

/** Mismo lenguaje visual que `OpportunityCard`: tono semántico, no decorativo. */
function Chip({
  label,
  tone = "muted",
}: {
  label: string;
  tone?: "muted" | "success" | "warning" | "destructive";
}) {
  const cls =
    tone === "success"
      ? "bg-success/10 text-success"
      : tone === "warning"
        ? "bg-warning/10 text-warning"
        : tone === "destructive"
          ? "bg-destructive/10 text-destructive"
          : "bg-muted text-muted-foreground";
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${cls}`}>{label}</span>
  );
}

function ContextTile({
  label,
  value,
  chip,
  sub,
}: {
  label: string;
  value: string;
  chip?: { label: string; tone: "muted" | "success" | "warning" | "destructive" };
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-xs text-muted-foreground mb-1.5">{label}</div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-sm font-semibold">{value}</span>
        {chip && <Chip label={chip.label} tone={chip.tone} />}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{sub}</div>}
    </div>
  );
}

const ORIGEN_LABEL: Record<string, string> = {
  licitacion: "Licitación pública",
  convenio_marco: "Convenio Marco",
  otro: "Sin identificar",
};

/** Todo lo que se ve acá ya viene resuelto del backend — el componente solo pinta. */
function ContextoEstrategico({ oc }: { oc: OrdenCompraDetalle }) {
  const ctx = oc.contextoEstrategico;

  const origenSub =
    ctx.origen.tipo === "convenio_marco"
      ? [ctx.origen.codigo, ctx.origen.vigenciaFin && `vigente hasta ${fmtFecha(ctx.origen.vigenciaFin)}`]
          .filter(Boolean)
          .join(" · ")
      : (ctx.origen.codigo ?? undefined);

  const prov = ctx.proveedorEnOrganismo;
  const conc = ctx.concentracionOrganismo;
  const bench = ctx.benchmark;

  return (
    <section className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <ContextTile
        label="Origen"
        value={ORIGEN_LABEL[ctx.origen.tipo]}
        sub={origenSub}
      />
      <ContextTile
        label="Proveedor en este organismo"
        value={prov ? `${prov.ordenes} ${prov.ordenes === 1 ? "compra" : "compras"}` : "—"}
        chip={
          prov
            ? prov.recurrente
              ? { label: "Recurrente", tone: "success" }
              : { label: "Primera compra", tone: "muted" }
            : undefined
        }
        sub={prov ? `${formatCLP(prov.montoAcumuladoCLP)} acumulado` : "Sin historial previo con este organismo"}
      />
      <ContextTile
        label="Concentración del organismo"
        value={conc ? `${conc.shareTopPct}%` : "—"}
        chip={
          conc
            ? {
                label: levelLabel(conc.nivel),
                tone: conc.nivel === "alto" ? "destructive" : conc.nivel === "medio" ? "warning" : "success",
              }
            : undefined
        }
        sub={conc ? `${conc.proveedorTop ?? "Proveedor top"} · ${conc.ordenes} órdenes, ${conc.proveedores} proveedores` : "Historial insuficiente para medir concentración"}
      />
      <ContextTile
        label="Vs. ticket promedio del organismo"
        value={bench ? `${bench.desviacionPct > 0 ? "+" : ""}${bench.desviacionPct}%` : "—"}
        chip={
          bench
            ? {
                label: bench.evaluacion === "sobre" ? "Sobre promedio" : bench.evaluacion === "bajo" ? "Bajo promedio" : "En línea",
                tone: bench.evaluacion === "sobre" ? "warning" : bench.evaluacion === "bajo" ? "success" : "muted",
              }
            : undefined
        }
        sub={bench ? `Promedio del organismo: ${formatCLP(bench.ticketPromedioOrganismoCLP)}` : "Historial insuficiente para comparar"}
      />
    </section>
  );
}

/**
 * Reusa el módulo de Competidores ya existente (adjudicaciones de
 * licitaciones, no ejecución de OC) filtrado a este organismo — sin backend
 * nuevo. Compacta a propósito: el detalle completo vive en /competidores.
 */
function CompetidoresOrganismo({ organismo }: { organismo: string }) {
  const competidores = useQuery({
    queryKey: ["competidores-organismo", organismo],
    queryFn: () => competitorsService.list({ organismo, orden: "share", pageSize: 5 }),
    staleTime: 5 * 60_000,
  });

  const items = competidores.data?.items ?? [];

  return (
    <section className="rounded-xl border border-border bg-card p-4 space-y-3">
      <h2 className="text-sm font-semibold flex items-center gap-1.5">
        <Swords className="size-4" />
        Competidores en este organismo
      </h2>
      {competidores.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Sin adjudicaciones registradas de otros proveedores para este organismo.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((c) => (
            <li key={c.id}>
              <Link
                to="/competidores/$id"
                params={{ id: c.id }}
                className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-xs hover:border-primary/40 hover:bg-accent/40 transition-colors"
              >
                <span className="min-w-0 truncate font-medium" title={c.nombre}>
                  {c.nombre}
                </span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {c.participacion}% · {formatCLP(c.montoAdjudicado)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <Button size="sm" variant="outline" className="w-full" asChild>
        <Link to="/competidores">
          Ver todos los competidores
          <ExternalLink className="size-3.5 ml-1" />
        </Link>
      </Button>
    </section>
  );
}

function OrdenCompraDetallePage() {
  const { codigo } = useParams({ from: "/ordenes-compra/$codigo" });
  const qc = useQueryClient();

  const detalle = useQuery({
    queryKey: ["orden-compra", codigo],
    queryFn: () => ordenesCompraService.detalle(codigo),
    staleTime: 5 * 60_000,
  });

  const vincular = useMutation({
    mutationFn: (vars: { accion: "confirmar" | "descartar"; licitacionCodigo?: string | null }) =>
      ordenesCompraService.vincular(codigo, vars.accion, vars.licitacionCodigo),
    onSuccess: (_data, vars) => {
      toast.success(
        vars.accion === "confirmar" ? "Vínculo confirmado" : "Sugerencia descartada",
      );
      qc.invalidateQueries({ queryKey: ["orden-compra", codigo] });
      qc.invalidateQueries({ queryKey: ["ordenes-compra"] });
    },
    onError: () => toast.error("No pudimos actualizar el vínculo"),
  });

  const hidratar = useMutation({
    mutationFn: () => ordenesCompraService.hidratarLicitacion(codigo),
    onSuccess: (r) => {
      if (r.estado === "no-disponible") {
        toast.error("Mercado Público no entrega esta licitación (histórica o dada de baja)");
      } else {
        toast.success("Licitación de origen sincronizada");
      }
      qc.invalidateQueries({ queryKey: ["orden-compra", codigo] });
      qc.invalidateQueries({ queryKey: ["ordenes-compra"] });
    },
    onError: () => toast.error("No pudimos traer la licitación desde Mercado Público"),
  });

  const oc = detalle.data;

  return (
    <AppShell title={`Orden de compra ${codigo}`} subtitle={oc?.nombre ?? "Detalle de la orden"}>
      <div className="mb-4">
        <Link
          to="/ordenes-compra"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Volver a órdenes de compra
        </Link>
      </div>

      {detalle.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : detalle.isError || !oc ? (
        <EmptyState
          icon={ShoppingCart}
          title="No encontramos esta orden de compra"
          description="Puede que aún no se haya sincronizado desde Mercado Público."
          action={<Button onClick={() => detalle.refetch()}>Reintentar</Button>}
        />
      ) : (
        <>
        <ContextoEstrategico oc={oc} />
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            <section className="rounded-xl border border-border bg-card p-4 space-y-4">
              <h2 className="text-sm font-semibold">Resumen</h2>
              <div className="grid gap-3 sm:grid-cols-3">
                <Dato label="Estado" value={legibleEstado(oc.estadoMP)} />
                <Dato label="Tipo de compra" value={legibleTipo(oc.tipoCompra)} />
                <Dato label="Emisión" value={fmtFecha(oc.fechaEnvio)} />
                <Dato label="Creación" value={fmtFecha(oc.fechaCreacion)} />
                <Dato label="Aceptación" value={fmtFecha(oc.fechaAceptacion)} />
                <Dato label="Moneda" value={oc.moneda} />
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <KpiTile label="Neto" value={fmtMonto(oc.totalNeto)} />
                <KpiTile label="Impuestos" value={fmtMonto(oc.impuestos)} />
                <KpiTile label="Total" value={fmtMonto(oc.total)} accent />
              </div>
            </section>

            <section className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="p-4 pb-3">
                <h2 className="text-sm font-semibold">Ítems ({oc.items.length})</h2>
                {oc.contextoEstrategico.rubroPrincipal && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Rubro principal: <span className="font-medium text-foreground">{oc.contextoEstrategico.rubroPrincipal.nombre}</span> — el organismo ha gastado{" "}
                    {formatCLP(oc.contextoEstrategico.rubroPrincipal.gastoOrganismoCLP)} en{" "}
                    {oc.contextoEstrategico.rubroPrincipal.ordenesOrganismo} órdenes de este rubro en los últimos 24 meses.
                  </p>
                )}
              </div>
              {oc.items.length === 0 ? (
                <p className="px-4 pb-4 text-sm text-muted-foreground">
                  Mercado Público no publicó el detalle de ítems para esta orden.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Producto</TableHead>
                      <TableHead className="text-right">Cantidad</TableHead>
                      <TableHead className="text-right">Precio unitario</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {oc.items.map((it, i) => (
                      <TableRow key={`${it.correlativo ?? i}`}>
                        <TableCell className="max-w-[24rem]">
                          <div className="text-sm font-medium line-clamp-2">
                            {it.producto ?? "—"}
                          </div>
                          {it.descripcion && (
                            <div className="text-xs text-muted-foreground line-clamp-2">
                              {it.descripcion}
                            </div>
                          )}
                          {it.categoria && (
                            <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                              {it.categoria}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-sm">
                          {it.cantidad ?? "—"} {it.unidad ?? ""}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-sm">
                          {it.precioUnitario != null ? formatCLP(it.precioUnitario) : "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-sm">
                          {it.total != null ? formatCLP(it.total) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </section>
          </div>

          <div className="space-y-4">
            <section className="rounded-xl border border-border bg-card p-4 space-y-3">
              <h2 className="text-sm font-semibold">Partes</h2>
              <Dato label="Organismo comprador" value={oc.organismo} />
              <Dato label="Unidad" value={oc.organismoUnidad ?? oc.organismoFicha?.unidad ?? "—"} />
              <Dato label="Proveedor" value={oc.proveedorNombre ?? "—"} />
              <Dato label="RUT proveedor" value={oc.proveedorRut ?? "—"} />
              {oc.proveedorFicha?.estado && (
                <Dato label="Estado del proveedor" value={oc.proveedorFicha.estado} />
              )}
              <Dato
                label="Ubicación"
                value={
                  [oc.comuna ?? oc.proveedorFicha?.comuna, oc.region ?? oc.proveedorFicha?.region]
                    .filter(Boolean)
                    .join(", ") || "—"
                }
              />
              {(oc.proveedorFicha?.direccion || oc.organismoFicha?.direccion) && (
                <Dato
                  label="Dirección"
                  value={oc.proveedorFicha?.direccion || oc.organismoFicha?.direccion || "—"}
                />
              )}
            </section>

            <section className="rounded-xl border border-border bg-card p-4 space-y-3">
              <h2 className="text-sm font-semibold flex items-center gap-1.5">
                <FileText className="size-4" />
                Origen
              </h2>
              {!oc.licitacion && oc.licitacionCodigo && oc.vinculoOrigen !== "descartado" ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{oc.licitacionCodigo}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      No sincronizada
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    La OC referencia esta licitación, pero todavía no está en nuestros datos
                    maestros (suele pasar con licitaciones históricas).
                    {oc.licitacionNoDisponibleAt
                      ? " Mercado Público no la entregó en el último intento."
                      : ""}
                  </p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      size="sm"
                      disabled={hidratar.isPending}
                      onClick={() => hidratar.mutate()}
                    >
                      <Download className="size-3.5 mr-1" />
                      {hidratar.isPending ? "Trayendo…" : "Traer desde Mercado Público"}
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                      <a
                        href={mercadoPublicoUrl(oc.licitacionCodigo)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ExternalLink className="size-3.5 mr-1" />
                        Ficha oficial
                      </a>
                    </Button>
                  </div>
                </div>
              ) : oc.licitacion ? (
                <div className="space-y-2">
                  <Link
                    to="/licitaciones/$id"
                    params={{ id: oc.licitacion.codigo }}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    {oc.licitacion.codigo}
                  </Link>
                  <p className="text-sm line-clamp-3">{oc.licitacion.nombre}</p>
                  <p className="text-xs text-muted-foreground">
                    {oc.vinculoOrigen === "sugerido"
                      ? `Vínculo sugerido (confianza ${oc.vinculoScore ?? 0}%) — confírmalo si corresponde.`
                      : oc.vinculoOrigen === "manual"
                        ? "Vínculo confirmado manualmente."
                        : "Vínculo publicado por Mercado Público."}
                  </p>
                  {oc.vinculoOrigen === "sugerido" && (
                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        disabled={vincular.isPending}
                        onClick={() =>
                          vincular.mutate({
                            accion: "confirmar",
                            licitacionCodigo: oc.licitacion?.codigo,
                          })
                        }
                      >
                        Confirmar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={vincular.isPending}
                        onClick={() => vincular.mutate({ accion: "descartar" })}
                      >
                        <X className="size-3.5 mr-1" />
                        Descartar
                      </Button>
                    </div>
                  )}
                </div>
              ) : oc.contextoEstrategico.origen.tipo === "convenio_marco" ? (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      {oc.contextoEstrategico.origen.nombre ?? oc.contextoEstrategico.origen.codigo}
                    </span>
                    <Chip label="Convenio Marco" tone="muted" />
                  </div>
                  {oc.contextoEstrategico.origen.codigo && (
                    <p className="text-xs font-mono text-muted-foreground">
                      {oc.contextoEstrategico.origen.codigo}
                    </p>
                  )}
                  {oc.contextoEstrategico.origen.vigenciaFin && (
                    <p className="text-xs text-muted-foreground">
                      Vigente hasta {fmtFecha(oc.contextoEstrategico.origen.vigenciaFin)}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Esta orden no proviene de una licitación competitiva (Convenio Marco o Compra
                  Ágil), o el vínculo aún no se ha identificado.
                </p>
              )}
            </section>

            <CompetidoresOrganismo organismo={oc.organismo} />

            {oc.sugerencias.length > 0 && (
              <section className="rounded-xl border border-border bg-card p-4 space-y-3">
                <h2 className="text-sm font-semibold flex items-center gap-1.5">
                  <Link2 className="size-4" />
                  Licitaciones candidatas
                </h2>
                {oc.sugerencias.map((s) => (
                  <div key={s.codigo} className="rounded-lg border border-border p-3 space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <Link
                        to="/licitaciones/$id"
                        params={{ id: s.codigo }}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        {s.codigo}
                      </Link>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {s.score}%
                      </span>
                    </div>
                    <p className="text-xs line-clamp-2">{s.nombre}</p>
                    <ul className="text-[11px] text-muted-foreground list-disc pl-4">
                      {s.motivos.map((m) => (
                        <li key={m}>{m}</li>
                      ))}
                    </ul>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      disabled={vincular.isPending}
                      onClick={() =>
                        vincular.mutate({ accion: "confirmar", licitacionCodigo: s.codigo })
                      }
                    >
                      Vincular a esta licitación
                    </Button>
                  </div>
                ))}
              </section>
            )}
          </div>
        </div>
        </>
      )}
    </AppShell>
  );
}
